/**
 * 極簡的資料取用 hook。刻意不引入 react-query——
 * §2.2 的精神是「降低依賴性」，而這個系統的查詢型態很簡單：
 * 讀一次、過帳後重抓受影響的查詢（§7.5）。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, NetworkError, api } from './client.js'

export interface QueryState<T> {
  data: T | null
  error: Error | null
  loading: boolean
  /** 過帳成功後呼叫，重抓受影響的查詢（§7.5） */
  reload: () => void
}

/**
 * @param path null = 先不要抓（條件還沒齊）
 * @param deps 額外的重抓依據
 */
export function useQuery<T>(path: string | null, deps: unknown[] = []): QueryState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(path !== null)
  const [tick, setTick] = useState(0)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  useEffect(() => {
    if (path === null) { setLoading(false); return }
    const ctrl = new AbortController()
    setLoading(true)
    api.get<T>(path, { signal: ctrl.signal })
      .then((d) => { if (alive.current) { setData(d); setError(null) } })
      .catch((e) => {
        if (ctrl.signal.aborted) return
        if (alive.current) setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => { if (alive.current && !ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data, error, loading, reload }
}

export interface MutationState<TArgs, TResult> {
  run: (args: TArgs) => Promise<TResult | null>
  /** 進行中。現場的按鈕要 disable，否則會按第二次（§10.3 有冪等保護，但畫面也該擋） */
  pending: boolean
  error: ApiError | NetworkError | Error | null
  reset: () => void
}

/**
 * 過帳用。
 *
 * §10.3：key 在第一次呼叫時產生，**失敗重試時沿用同一個**——
 * 這是「按了沒反應再按一次不能變成繳兩次庫」的關鍵。
 * 成功之後才換新的 key（下一次是不同的操作）。
 */
export function useMutation<TArgs, TResult>(
  fn: (args: TArgs, idempotencyKey: string) => Promise<TResult>,
): MutationState<TArgs, TResult> {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const keyRef = useRef<string | null>(null)

  const run = useCallback(async (args: TArgs) => {
    keyRef.current ??= crypto.randomUUID()
    setPending(true)
    setError(null)
    try {
      const out = await fn(args, keyRef.current)
      keyRef.current = null   // 成功 → 下一次操作用新的 key
      return out
    } catch (e) {
      // 失敗**不清掉 key**：重試時要用同一個
      setError(e instanceof Error ? e : new Error(String(e)))
      return null
    } finally {
      setPending(false)
    }
  }, [fn])

  const reset = useCallback(() => { setError(null); keyRef.current = null }, [])
  return { run, pending, error, reset }
}
